/**
 * LeadPerformanceModal (Jul-2026, User-Wunsch) — Erfolgsreport für Verkaufsleiter:
 * Reaktionszeit & Ergebnis je Verkäufer. Montags automatisch als Nachricht.
 */
import { useState, useEffect } from "react";
import { X, Loader2, BarChart3 } from "lucide-react";
import api from "../../lib/api";

const RANGES = [[7, "7 Tage"], [30, "30 Tage"], [90, "90 Tage"]];

export default function LeadPerformanceModal({ onClose }) {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/dealer/leads/performance-report", { params: { days } })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [days]);

    const sellers = data?.sellers || [];
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="lead-performance-modal">
            <div className="bg-white rounded-sm w-full max-w-3xl mt-10 mb-10 shadow-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-black text-[#111827] flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#FFCC00]" /> Lead-Erfolgsreport
                    </h2>
                    <div className="flex items-center gap-2">
                        {RANGES.map(([d, label]) => (
                            <button key={d} onClick={() => setDays(d)}
                                className={`text-xs font-bold px-2.5 py-1.5 rounded-sm border ${days === d ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "border-gray-300 hover:border-gray-500"}`}
                                data-testid={`lead-perf-range-${d}`}>
                                {label}
                            </button>
                        ))}
                        <button onClick={onClose} className="ml-2 text-gray-400 hover:text-[#111827]" data-testid="lead-perf-close"><X className="w-5 h-5" /></button>
                    </div>
                </div>
                <div className="p-5">
                    <p className="text-xs text-gray-500 mb-3">
                        Reaktionszeit = Zeit von Zuweisung bis zum ersten erfassten Kontakt. Der Report geht zusätzlich jeden Montag automatisch als Nachricht an Verkaufsleiter, Filialleiter und den Inhaber.
                    </p>
                    {loading ? (
                        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
                    ) : sellers.length === 0 ? (
                        <div className="text-center py-10 text-sm text-gray-500" data-testid="lead-perf-empty">Keine zugewiesenen Leads im Zeitraum.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="lead-perf-table">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                        <th className="text-left py-2 pr-3">Verkäufer</th>
                                        <th className="text-right py-2 px-2">Leads</th>
                                        <th className="text-right py-2 px-2">Kontaktiert</th>
                                        <th className="text-right py-2 px-2">Ø Reaktion</th>
                                        <th className="text-right py-2 px-2" title="Rote Ampeln im Zeitraum">🔴 Rot</th>
                                        <th className="text-right py-2 px-2">Gewonnen</th>
                                        <th className="text-right py-2 px-2">Verloren</th>
                                        <th className="text-right py-2 px-2">Offen</th>
                                        <th className="text-right py-2 px-2" title="Leads mit KI-Coach-Nutzung → davon gewonnen">KI-Coach</th>
                                        <th className="text-right py-2 pl-2">Quote</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sellers.map((s) => (
                                        <tr key={s.seller_id} className="border-b border-gray-100" data-testid={`lead-perf-row-${s.seller_id}`}>
                                            <td className="py-2 pr-3 font-bold">{s.seller_name}</td>
                                            <td className="text-right px-2 font-mono-num">{s.leads}</td>
                                            <td className="text-right px-2 font-mono-num">{s.contacted}</td>
                                            <td className={`text-right px-2 font-mono-num font-bold ${s.avg_reaction_h == null ? "text-gray-400" : s.avg_reaction_h <= 2 ? "text-emerald-600" : s.avg_reaction_h <= 4 ? "text-amber-600" : "text-red-600"}`}>
                                                {s.avg_reaction_h == null ? "—" : `${s.avg_reaction_h} Std.`}
                                            </td>
                                            <td className={`text-right px-2 font-mono-num ${s.red_count ? "text-red-600 font-bold" : "text-gray-400"}`}>{s.red_count}</td>
                                            <td className="text-right px-2 font-mono-num text-emerald-700">{s.won}</td>
                                            <td className="text-right px-2 font-mono-num text-gray-600">{s.lost}</td>
                                            <td className="text-right px-2 font-mono-num">{s.open}</td>
                                            <td className="text-right px-2 font-mono-num" title={s.ki_used ? `${s.ki_used} Leads mit KI-Coach, davon ${s.ki_won} gewonnen${s.ki_quote != null ? ` (${s.ki_quote} %)` : ""}` : "Kein KI-Coach-Einsatz im Zeitraum"} data-testid={`lead-perf-ki-${s.seller_id}`}>
                                                {s.ki_used ? <span className={s.ki_won ? "text-emerald-700 font-bold" : "text-gray-600"}>{s.ki_won}/{s.ki_used}</span> : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="text-right pl-2 font-mono-num font-bold">{s.quote} %</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {/* Aug-2026 (User-Wunsch) — Verlust-Auswertung: häufigste Schließ-Gründe */}
                    {!loading && (data?.loss_reasons || []).length > 0 && (
                        <div className="mt-5" data-testid="lead-perf-loss-reasons">
                            <h3 className="text-xs font-black uppercase tracking-wider text-[#111827] mb-2">
                                Verlust-Gründe im Zeitraum (Top {(data.loss_reasons || []).length})
                            </h3>
                            <div className="space-y-1.5">
                                {(() => {
                                    const max = Math.max(...data.loss_reasons.map((r) => r.count), 1);
                                    const total = data.loss_reasons.reduce((s, r) => s + r.count, 0);
                                    return data.loss_reasons.map((r) => (
                                        <div key={r.reason} className="flex items-center gap-2 text-xs" data-testid={`lead-perf-loss-${r.reason}`}>
                                            <span className="w-44 truncate font-bold text-gray-700" title={r.reason}>{r.reason}</span>
                                            <div className="flex-1 h-4 bg-gray-100 rounded-sm overflow-hidden">
                                                <div className="h-full bg-red-400/80" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
                                            </div>
                                            <span className="w-16 text-right font-mono-num font-bold">{r.count} <span className="text-gray-400 font-normal">({Math.round((r.count / total) * 100)} %)</span></span>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                    )}
                    {!loading && (data?.loss_reasons || []).length === 0 && sellers.length > 0 && (
                        <p className="mt-4 text-[11px] text-gray-400" data-testid="lead-perf-loss-empty">
                            Keine verlorenen Leads mit Schließ-Grund im Zeitraum.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
